import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Crown, Loader2, Music } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useIsAndroidApp } from "@/hooks/useLaunchContext";
import { SONG_UNIT_PRICE_EUR } from "@/utils/price";
import { canUseGooglePlayBilling } from "@/utils/pwaContext";
import { subscribeViaGooglePlay } from "@/hooks/useSubscription";
import MollieCardPayment from "@/components/wallet/MollieCardPayment";
import { usePromoActive } from "@/components/shared/PromoBanner";
import api from "@/lib/api";
import { SUBSCRIPTION } from "@/constants/testIds";

const FEATURE_KEYS = ["feat1", "feat2", "feat3", "feat4", "feat5", "feat6", "feat7", "feat8", "feat9"];

/**
 * The 2 monthly plan cards (Plus / Premium highlighted). Used on the Checkout recap (above
 * the token packs) and in the dashboard "Abonnement" tab. Inside the Android app (TWA) no
 * currency is EVER shown - Google Play displays its own localized price on the native sheet.
 */
export default function SubscriptionPlans({ resumeOrderId, onSubscribed, compact = false, onCardOpen, collapseSignal = 0 }) {
  const { t, language } = useI18n();
  const { toast } = useToast();
  const { user, guestCheckout } = useAuth();
  const isTwaApp = useIsAndroidApp();
  // Active promo (e.g. SUMMER2026 -50%) discounts the FIRST month only - renewals are always
  // full price. Web only: inside the Android app Google Play owns pricing (intro-price offer
  // configured in Play Console), so no promo/currency is ever displayed there.
  const promo = usePromoActive();
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [cardPlan, setCardPlan] = useState(null);
  // Guest express flow (17/08/2026): a visitor subscribes with JUST their email - the account
  // is auto-created behind the scenes (existing POST /auth/guest), the subscription attaches
  // to it, and an access email (magic link + optional set-password) is sent on activation.
  const [guestPlan, setGuestPlan] = useState(null);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestBusy, setGuestBusy] = useState(false);
  const [guestError, setGuestError] = useState("");
  const [guestReady, setGuestReady] = useState(false);

  useEffect(() => {
    api.get("/subscriptions/plans").then(({ data }) => setPlans(data.plans || []));
  }, []);

  // Mutual exclusivity with the token-pack card form on the checkout page: only ONE payment
  // form is ever open at a time (the parent bumps collapseSignal when a pack is picked).
  useEffect(() => {
    if (collapseSignal) {
      setCardPlan(null);
      setGuestPlan(null);
    }
  }, [collapseSignal]);

  const openCardForm = (plan) => {
    onCardOpen?.();
    setGuestPlan(null);
    setCardPlan(plan);
  };

  const handleSubscribe = async (plan) => {
    // Not logged in: 1-field express signup instead of a login/register wall (0 lost sales).
    if (!user) {
      onCardOpen?.();
      setCardPlan(null);
      setGuestPlan(plan);
      setGuestError("");
      setGuestReady(false);
      return;
    }
    setLoadingPlan(plan.plan_id);
    // Android/TWA: native Google Play subscription sheet, exactly like token packs.
    if (await canUseGooglePlayBilling()) {
      try {
        const res = await subscribeViaGooglePlay({ ...plan, promo_code: promo?.code || null });
        if (!res?.redirected) {
          toast({ title: t("subscription.subscribedToast") });
          onSubscribed?.();
        }
      } catch (err) {
        if (err?.playBillingFallback) {
          toast({ title: t("checkout.playBillingFallbackTitle"), description: t("checkout.playBillingFallbackDesc") });
          openCardForm(plan);
        } else if (err?.isPurchaseCancelled) {
          toast({ title: t("checkout.playCancelledTitle"), description: t("checkout.playCancelledDesc") });
        } else {
          toast({ variant: "destructive", title: t("common.error"), description: err?.response?.data?.detail || err?.message });
        }
      } finally {
        setLoadingPlan(null);
      }
      return;
    }
    // Web: embedded Mollie card form (first payment = month 1 + mandate for auto-renewal).
    openCardForm(plan);
    setLoadingPlan(null);
  };

  const handleGuestContinue = async (e) => {
    e.preventDefault();
    setGuestBusy(true);
    setGuestError("");
    try {
      await guestCheckout(guestEmail.trim().toLowerCase());
      if (isTwaApp) {
        // Android: the Google Play sheet needs a FRESH user click (transient activation) -
        // never auto-opened after an await. A "Pay now" button is shown instead.
        setGuestReady(true);
      } else {
        openCardForm(guestPlan);
      }
    } catch (err) {
      setGuestError(err?.response?.data?.detail || err?.message);
    } finally {
      setGuestBusy(false);
    }
  };

  const handleGuestPayNow = () => {
    const plan = guestPlan;
    setGuestPlan(null);
    setGuestReady(false);
    handleSubscribe(plan);
  };

  const priceLabel = (value) =>
    language === "fr" ? `${value.toFixed(2).replace(".", ",")}€` : `$${value.toFixed(2)}`;

  const firstMonthPrice = (plan) => {
    // Integer-cent math with floor on half cents - MUST stay identical to first_month_price()
    // in backend/subscriptions.py so the displayed and charged amounts never diverge.
    if (!promo) return null;
    const cents = Math.round(plan.price_eur * 100);
    let discounted = null;
    if (promo.percent_off) discounted = Math.floor((cents * (100 - promo.percent_off)) / 100);
    else if (promo.amount_off) discounted = cents - Math.round(promo.amount_off * 100);
    return discounted == null ? null : Math.max(1, discounted) / 100;
  };

  return (
    <div data-testid={SUBSCRIPTION.plansContainer}>
      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2 lg:grid-cols-4"}`}>
        {plans.map((plan) => {
          const isFeatured = plan.featured;
          const firstPrice = firstMonthPrice(plan);
          return (
            <div
              key={plan.plan_id}
              data-testid={SUBSCRIPTION.planCard(plan.plan_id)}
              className={`relative rounded-2xl border p-5 ${
                isFeatured ? "border-primary bg-primary/5 shadow-[0_0_24px_-6px_hsl(var(--primary)/0.5)]" : "border-border bg-card"
              }`}
            >
              {isFeatured && (
                <span className="absolute -top-2.5 left-1/2 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full bg-primary px-3 py-0.5 text-[10px] font-semibold text-primary-foreground">
                  <Crown className="h-3 w-3" />
                  {t("subscription.featured")}
                </span>
              )}
              <p className="font-heading text-lg font-semibold">{plan.name}</p>
              {isTwaApp ? (
                <p className="mt-1 text-xs text-muted-foreground">{t("subscription.priceOnPlay")}</p>
              ) : firstPrice != null ? (
                <div className="mt-1">
                  <p className="font-heading text-2xl font-semibold text-primary">
                    {priceLabel(firstPrice)}
                    <span className="ml-1.5 text-sm font-normal text-muted-foreground line-through">{priceLabel(plan.price_eur)}</span>
                    <span className="text-sm font-normal text-muted-foreground"> {t("subscription.firstMonth")}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t("subscription.thenPrice")} {priceLabel(plan.price_eur)}{t("subscription.perMonth")}
                  </p>
                  <span className="mt-1.5 inline-block rounded-full bg-success/10 px-2.5 py-0.5 text-[10px] font-semibold text-success" data-testid={`subscription-promo-badge-${plan.plan_id}`}>
                    {promo.code} · {promo.percent_off ? `-${promo.percent_off}%` : `-${priceLabel(promo.amount_off)}`} {t("subscription.promoFirstMonthOnly")}
                  </span>
                </div>
              ) : (
                <p className="mt-1 font-heading text-2xl font-semibold text-primary">
                  {priceLabel(plan.price_eur)}
                  <span className="text-sm font-normal text-muted-foreground">{t("subscription.perMonth")}</span>
                </p>
              )}
              <p className="mt-2 flex items-center gap-1.5 text-sm font-medium">
                <Music className="h-4 w-4 shrink-0 text-primary" />
                {plan.monthly_quota} {t("subscription.musicsPerMonth")}
              </p>
              {!isTwaApp && (
                <div className="mt-1">
                  <p className="text-xs font-semibold text-success" data-testid={`subscription-per-song-${plan.plan_id}`}>
                    {t("subscription.perSongEquiv").replace("{price}", priceLabel(Math.floor((plan.price_eur / plan.monthly_quota) * 100) / 100))}
                  </p>
                  <span className="mt-1.5 inline-block rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold text-primary" data-testid={`subscription-savings-badge-${plan.plan_id}`}>
                    -{Math.round((1 - plan.price_eur / plan.monthly_quota / SONG_UNIT_PRICE_EUR) * 100)}% {t("subscription.savingsVsUnit")}
                  </span>
                </div>
              )}
              {!compact && (
                <>
                  <p className="mt-3 text-xs font-medium text-muted-foreground">{t("subscription.allFeatures")}</p>
                  <ul className="mt-2 space-y-1">
                    {FEATURE_KEYS.map((k) => (
                      <li key={k} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Check className="h-3 w-3 shrink-0 text-success" />
                        {t(`subscription.${k}`)}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <p className="mt-3 text-xs font-semibold text-primary">{t("subscription.noCredits")}</p>
              <button
                type="button"
                data-testid={SUBSCRIPTION.subscribeBtn(plan.plan_id)}
                onClick={() => handleSubscribe(plan)}
                disabled={!!loadingPlan}
                className={`mt-4 flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold transition-all disabled:opacity-60 ${
                  isFeatured
                    ? "bg-primary text-primary-foreground hover:opacity-90"
                    : "border border-primary text-primary hover:bg-primary/10"
                }`}
              >
                {loadingPlan === plan.plan_id && <Loader2 className="h-4 w-4 animate-spin" />}
                {loadingPlan === plan.plan_id ? t("subscription.subscribing") : t("subscription.subscribe")}
              </button>
            </div>
          );
        })}
      </div>
      {guestPlan && !cardPlan && (
        <div className="mt-4 rounded-2xl border border-primary/40 bg-card p-5 animate-in fade-in slide-in-from-top-2" data-testid="subscription-guest-gate">
          <p className="text-sm font-semibold">
            {t("subscription.guestTitle")} — <span className="text-primary">{guestPlan.name}</span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{t("subscription.guestDesc")}</p>
          {guestReady ? (
            <div className="mt-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-success">
                <Check className="h-4 w-4" />
                {t("subscription.guestAccountReady")}
              </p>
              <button
                type="button"
                data-testid="subscription-guest-pay-btn"
                onClick={handleGuestPayNow}
                className="mt-3 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90"
              >
                {t("subscription.guestPayNow")}
              </button>
            </div>
          ) : (
            <form onSubmit={handleGuestContinue} className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                required
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                placeholder={t("subscription.guestEmailPlaceholder")}
                data-testid="subscription-guest-email-input"
                className="h-10 flex-1 rounded-full border border-border bg-background px-4 text-sm outline-none transition-colors focus:border-primary"
              />
              <button
                type="submit"
                disabled={guestBusy || !guestEmail.trim()}
                data-testid="subscription-guest-continue-btn"
                className="flex h-10 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-60"
              >
                {guestBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("subscription.guestContinue")}
              </button>
            </form>
          )}
          {guestError && (
            <p className="mt-2 text-xs text-destructive" data-testid="subscription-guest-error">
              {guestError}{" "}
              <Link to="/login" className="font-medium underline underline-offset-2">
                {t("subscription.guestLoginLink")}
              </Link>
            </p>
          )}
        </div>
      )}
      {cardPlan && (
        <MollieCardPayment
          pack={0}
          subscriptionPlan={{
            ...cardPlan,
            promo_code: !isTwaApp && promo ? promo.code : null,
            first_price_eur: !isTwaApp ? firstMonthPrice(cardPlan) : null,
          }}
          resumeOrderId={resumeOrderId}
          onSuccess={() => {
            setCardPlan(null);
            toast({ title: t("subscription.subscribedToast") });
            onSubscribed?.();
          }}
          onCancel={() => setCardPlan(null)}
        />
      )}
    </div>
  );
}
